import React, { useEffect } from 'react';
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";
import { useWorkflowStore } from "../../store/useWorkflowStore";
import InstanceSelector from "./selectors/InstanceSelector";
import ClientSelector from "./selectors/ClientSelector";
import DatabaseSelector from "./selectors/DatabaseSelector";
import FacilitySelector from "./selectors/FacilitySelector";
import { UseCaseSelector } from "./selectors/UseCaseSelector";
import { AuthorSelector } from "./selectors/AuthorSelector";
import { useToast } from "../../lib/use-toast";

/**
 * TargetInstanceForm component
 * 
 * Form for selecting target instance details for process migration
 */
interface TargetInstanceFormProps {
  onNext: () => void;
}

const TargetInstanceForm: React.FC<TargetInstanceFormProps> = ({ onNext }) => {
  const { 
    isFormValid, 
    setIsFormValid, 
    targetServiceId, 
    targetClient, 
    formData, 
    targetFacilityId, 
    targetUseCaseId,
    targetAuthorId
  } = useWorkflowStore();
  
  const { toast } = useToast();

  // Update form validity based on all required selections
  useEffect(() => {
    const isValid = 
      !!formData["targetHost"] && 
      !!targetServiceId && 
      !!targetClient && 
      !!formData["targetDatabaseName"] && 
      !!targetFacilityId && 
      !!targetUseCaseId &&
      !!targetAuthorId;
    
    setIsFormValid(isValid);
  }, [
    targetServiceId, 
    targetClient, 
    formData, 
    targetFacilityId, 
    targetUseCaseId,
    targetAuthorId,
    setIsFormValid
  ]);
  
  // Handle form submission
  const handleNext = () => {
    if (!isFormValid) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill in all required fields",
      });
      return;
    }
    
    // All validations passed, proceed to next step
    onNext();
  };
  
  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-4">Target</h2>
      <p className="text-muted-foreground mb-6">
        Select the target instances for the migration.
      </p>

      <div className="flex flex-col space-y-4">
        <InstanceSelector isTarget={true} label="Instance" />
        <ClientSelector isTarget={true} label="Client" />
        <DatabaseSelector isTarget={true} label="Database" />
        
        {/* Facility and Use Case selectors in 50/50 layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FacilitySelector isTarget={true} className="w-full" label="Facility" />
          <UseCaseSelector isTarget={true} className="w-full" label="Use Case" />
        </div>
        
        {/* Author selector */}
        <AuthorSelector className="w-full" />
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={handleNext}
          disabled={false}
          variant={isFormValid ? "default" : "outline"}
          className="px-6"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default TargetInstanceForm;
